import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Heart, ShoppingCart, ClipboardList, Store, Bike,
  MessageCircle, ChevronRight, Copy, Check, LogOut, Coins, Share2,
  BadgeCheck, CheckCircle2, User, MapPin, CreditCard,
  Plus, Pencil, Trash2, Mail, Star, Wallet, Upload, Clock,
  ChevronDown, X, Lock, QrCode,
} from "lucide-react";
import QRCode from "react-qr-code";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";
import MobileBottomBar from "../components/MobileBottomBar";
import { getImageUrl } from "../utils/getImageUrl";
import { shareContent } from "../utils/share";
import AddressForm from "../components/forms/AddressForm";
import PaymentForm from "../components/forms/PaymentForm";
import ChangePasswordForm from "../components/forms/ChangePasswordForm";

// ── Constantes ─────────────────────────────────────────────────
const COUNTRY_CODES = [
  { code: "+57", flag: "🇨🇴" }, { code: "+58", flag: "🇻🇪" },
  { code: "+1",  flag: "🇺🇸" }, { code: "+52", flag: "🇲🇽" },
  { code: "+54", flag: "🇦🇷" }, { code: "+55", flag: "🇧🇷" },
  { code: "+56", flag: "🇨🇱" }, { code: "+51", flag: "🇵🇪" },
  { code: "+593",flag: "🇪🇨" }, { code: "+591",flag: "🇧🇴" },
  { code: "+34", flag: "🇪🇸" }, { code: "+44", flag: "🇬🇧" },
];
const PAYMENT_ICONS  = { tarjeta: "💳", nequi: "🟣", daviplata: "🔴", efectivo: "💵", bancolombia: "🟡" };
const PAYMENT_LABELS = { tarjeta: "Tarjeta", nequi: "Nequi", daviplata: "Daviplata", efectivo: "Efectivo", bancolombia: "Bancolombia" };
const getId = (obj) => obj?.id ?? obj?._id;
const extractUser = (d) => d?.data?.user ?? d?.user ?? d;
const inp = "w-full p-3 rounded-xl border border-gray-200 focus:border-supernova-400 focus:ring-2 focus:ring-supernova-100 outline-none transition text-sm bg-white";

// ── Componente acordeón reutilizable ────────────────────────────
function Section({ icon, title, badge, summary, open, onToggle, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <button onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-4 hover:bg-gray-50 transition text-left">
        <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">{title}</p>
          {summary && <p className="text-xs text-gray-400 mt-0.5 truncate">{summary}</p>}
        </div>
        {badge && (
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">
            {badge}
          </span>
        )}
        <ChevronDown size={16} className={`text-gray-300 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-5 border-t border-gray-50 pt-4">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Ítem de menú simple (solo navegación) ───────────────────────
function MenuItem({ icon, iconBg, label, desc, to, onClick }) {
  const cls = "flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition border-b border-gray-50 last:border-0";
  const inner = (
    <>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 leading-tight">{label}</p>
        {desc && <p className="text-[11px] text-gray-400 leading-tight">{desc}</p>}
      </div>
      <ChevronRight size={14} className="text-gray-300 flex-shrink-0" />
    </>
  );
  if (to)     return <Link to={to} className={cls}>{inner}</Link>;
  if (onClick) return <button onClick={onClick} className={`w-full text-left ${cls}`}>{inner}</button>;
  return null;
}

// ── Componente principal ────────────────────────────────────────
export default function ClientDashboard() {
  const navigate  = useNavigate();
  const docInputRef = useRef(null);

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [openSec,  setOpenSec]  = useState(null); // 'profile'|'addresses'|'payments'|'identity'|'security'
  const toggleSec = (s) => setOpenSec(prev => prev === s ? null : s);

  // ── Datos de usuario ──
  const [user,      setUser]     = useState(null);
  const [form,      setForm]     = useState({
    name: "", phone: "", phoneCountryCode: "+57",
    documentType: "CC", documentNumber: "", birthDate: "",
  });

  // ── Colecciones ──
  const [addresses, setAddresses] = useState([]);
  const [payments,  setPayments]  = useState([]);

  // ── Dirección / pago edición ──
  const [addrOpen,  setAddrOpen]  = useState(null); // null | 'new' | id
  const [addrModalOpen, setAddrModalOpen] = useState(false); // modal automático sin dirección
  const [payOpen,   setPayOpen]   = useState(null);
  const [delConfirm, setDelConfirm] = useState(null); // { type, id }

  // ── Referidos ──
  const [refCode,  setRefCode]  = useState("");
  const [copied,   setCopied]   = useState(false);

  // ── QR ──
  const [qrOpen,   setQrOpen]   = useState(false);

  // ── OTP / verificación ──
  const [otpOpen,    setOtpOpen]    = useState(false);
  const [otpCode,    setOtpCode]    = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending,  setResending]  = useState(false);
  const [docLoading, setDocLoading] = useState(false);

  // ── Carga perfil ──────────────────────────────────────────────
  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await API.get("/users/me");
      const u = res.data;
      setUser(u);
      setForm({
        name:             u.name             || "",
        phone:            u.phone            || "",
        phoneCountryCode: u.phoneCountryCode || "+57",
        documentType:     u.documentType     || "CC",
        documentNumber:   u.documentNumber   || "",
        birthDate:        u.birthDate        ? u.birthDate.slice(0, 10) : "",
      });
      const addrs = Array.isArray(u.addresses) ? u.addresses : [];
      setAddresses(addrs);
      setPayments( Array.isArray(u.paymentMethods) ? u.paymentMethods : []);
      localStorage.setItem("user", JSON.stringify(u));
      // Show add-address modal on first load if user has no addresses
      if (addrs.length === 0) setAddrModalOpen(true);
    } catch {
      try {
        const r2 = await API.get("/auth/me");
        setUser(extractUser(r2.data));
      } catch { toast.error("No se pudo cargar tu perfil"); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  // ── Handlers: perfil ─────────────────────────────────────────
  const savePersonal = async () => {
    setSaving(true);
    try {
      await API.put("/users/me", {
        name: form.name, documentType: form.documentType,
        documentNumber: form.documentNumber,
        phone: form.phone, phoneCountryCode: form.phoneCountryCode,
        birthDate: form.birthDate || null,
      });
      toast.success("Perfil actualizado");
      await fetchProfile();
    } catch (err) { toast.error(err.response?.data?.message || "Error actualizando perfil"); }
    finally { setSaving(false); }
  };

  const resendEmail = async () => {
    setResending(true);
    try {
      await API.post("/auth/resend-verification");
      toast.success("Correo de verificación enviado");
    } catch (err) { toast.error(err.response?.data?.message || "No se pudo enviar"); }
    finally { setResending(false); }
  };

  const sendOtp = async () => {
    if (!form.phone || form.phone.replace(/\D/g,"").length < 7) {
      toast.warn("Ingresa un número válido primero"); return;
    }
    setOtpLoading(true);
    try {
      await API.post("/users/me/phone/send-otp", {
        phone: form.phone.replace(/\D/g,""),
        phoneCountryCode: form.phoneCountryCode,
      });
      toast.success("Código enviado por WhatsApp");
      setOtpOpen(true); setOtpCode("");
    } catch (err) { toast.error(err.response?.data?.message || "No se pudo enviar el código"); }
    finally { setOtpLoading(false); }
  };

  const verifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) { toast.warn("Ingresa el código de 6 dígitos"); return; }
    setOtpLoading(true);
    try {
      const res = await API.post("/users/me/phone/verify-otp", {
        code: otpCode,
        phone: form.phone.replace(/\D/g,""),
        phoneCountryCode: form.phoneCountryCode,
      });
      const pointsAwarded = res.data?.data?.pointsAwarded ?? 0;
      if (pointsAwarded > 0) {
        toast.success(`✅ ¡WhatsApp verificado! Ganaste ${pointsAwarded} QPoints 🎉`);
      } else {
        toast.success("¡WhatsApp verificado!");
      }
      setOtpOpen(false);
      await fetchProfile();
    } catch (err) { toast.error(err.response?.data?.message || "Código incorrecto"); }
    finally { setOtpLoading(false); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.warn("Máximo 10 MB"); return; }
    setDocLoading(true);
    try {
      const fd = new FormData(); fd.append("document", file);
      await API.post("/users/me/document", fd);
      toast.success("Documento enviado, lo revisaremos pronto.");
      await fetchProfile();
    } catch (err) { toast.error(err.response?.data?.message || "Error subiendo documento"); }
    finally { setDocLoading(false); }
  };

  // ── Handlers: direcciones ─────────────────────────────────────
  const submitAddr = async (data) => {
    try {
      if (addrOpen && addrOpen !== "new")
        await API.put(`/users/me/address/${addrOpen}`, data);
      else
        await API.post("/users/me/address", data);
      toast.success("Dirección guardada");
      setAddrOpen(null); await fetchProfile();
    } catch { toast.error("Error guardando dirección"); }
  };

  // ── Handlers: pagos ────────────────────────────────────────────
  const submitPay = async (data) => {
    try {
      if (payOpen && payOpen !== "new")
        await API.put(`/users/me/payment/${payOpen}`, data);
      else
        await API.post("/users/me/payment", data);
      toast.success("Método guardado");
      setPayOpen(null); await fetchProfile();
    } catch { toast.error("Error guardando método"); }
  };

  const doDelete = async () => {
    if (!delConfirm) return;
    try {
      if (delConfirm.type === "address")
        await API.delete(`/users/me/address/${delConfirm.id}`);
      else
        await API.delete(`/users/me/payment/${delConfirm.id}`);
      setDelConfirm(null); await fetchProfile();
    } catch { toast.error("Error eliminando"); }
  };

  // ── Handlers: referidos ───────────────────────────────────────
  const copyCode = () => {
    navigator.clipboard.writeText(user.referralCode);
    setCopied(true); toast.success("Código copiado");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = async () => {
    const url = `${window.location.origin}/register?ref=${user.referralCode}`;
    try {
      const result = await shareContent({
        title: "Únete a Qkiere.com",
        text: `Regístrate con mi código ${user.referralCode} y gana QPUNTOS 🎁`,
        url,
      });
      if (result === "copied") toast.success("Enlace copiado");
    } catch {}
  };

  const generateCode = async () => {
    try {
      const { data } = await API.post("/referrals/generate");
      const rc = data?.data?.referralCode ?? data?.referralCode;
      toast.success("Código generado");
      setUser(prev => ({ ...prev, referralCode: rc }));
    } catch (err) { toast.error(err?.response?.data?.message || "Error generando código"); }
  };

  const applyCode = async () => {
    try {
      await API.post("/referrals/apply", { code: refCode });
      toast.success("Código aplicado");
      setRefCode(""); await fetchProfile();
    } catch (err) { toast.error(err?.response?.data?.message || "Error aplicando código"); }
  };

  const logout = () => {
    ["token","accessToken","refreshToken","user"].forEach(k => localStorage.removeItem(k));
    navigate("/");
  };

  const openSupport = () => {
    const msg = user ? `Hola, soy ${user.name} (${user.email}). Necesito ayuda.` : "Hola, necesito ayuda.";
    window.open(`https://wa.me/584147570074?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // ── Valores calculados ────────────────────────────────────────
  const initials = user?.name
    ? user.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  const pendingTasks = user ? [
    !user.whatsappVerified && {
      id: "wa", icon: <MessageCircle size={15} />, color: "green",
      label: "Confirmar WhatsApp",
      hint: "Necesario para recibir notificaciones y pedidos",
      cta: "Verificar", action: () => { setOpenSec("profile"); },
    },
    addresses.length === 0 && {
      id: "addr", icon: <MapPin size={15} />, color: "blue",
      label: "Agregar dirección de entrega",
      hint: "Necesaria para recibir tus pedidos",
      cta: "Agregar", action: () => { setOpenSec("addresses"); setAddrOpen("new"); },
    },
    !user.emailVerified && {
      id: "email", icon: <Mail size={15} />, color: "purple",
      label: "Verificar correo electrónico",
      hint: "Revisa tu bandeja de entrada",
      cta: "Reenviar", action: resendEmail,
    },
  ].filter(Boolean) : [];

  const completedCount = user ? [
    user.whatsappVerified,
    addresses.length > 0,
    user.emailVerified,
  ].filter(Boolean).length : 0;
  const progress = user ? Math.round((completedCount / 3) * 100) : 0;

  // ── Loading ───────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-8 h-8 border-4 border-supernova-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-gray-50 pb-28">

      {/* ══ HEADER ══════════════════════════════════════════════ */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-950 pt-6 pb-12 px-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">

          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {user?.avatar ? (
              <img src={user.avatar} alt="Avatar"
                className="w-14 h-14 rounded-2xl object-cover border-2 border-white/20 shadow-lg" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-supernova-400 flex items-center justify-center text-xl font-bold text-black shadow-lg">
                {initials}
              </div>
            )}
            {user?.verificationStatus === "aprobado" && (
              <div className="absolute -bottom-1.5 -right-1.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow border-2 border-gray-900">
                <BadgeCheck size={11} className="text-white" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-bold text-white truncate leading-tight">
              {user?.name || "Mi cuenta"}
            </h1>
            <p className="text-white/40 text-xs truncate">{user?.email}</p>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {user?.whatsappVerified && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-green-500/20 text-green-300 px-2 py-0.5 rounded-full">
                  <CheckCircle2 size={9} /> WhatsApp
                </span>
              )}
              {user?.emailVerified && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full">
                  <Mail size={9} /> Email
                </span>
              )}
            </div>
          </div>

          {/* Progreso circular + logout */}
          <div className="flex flex-col items-center gap-2 flex-shrink-0">
            <div className="relative w-11 h-11">
              <svg className="w-11 h-11 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3.2" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#fbbf24" strokeWidth="3.2"
                  strokeDasharray={`${progress} ${100 - progress}`} strokeLinecap="round" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">{progress}%</span>
            </div>
            <button onClick={logout}
              className="flex items-center gap-1 text-[11px] text-white/40 hover:text-red-400 font-semibold transition">
              <LogOut size={11} /> Salir
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 -mt-6 space-y-3">

        {/* ══ QPUNTOS ══════════════════════════════════════════ */}
        <div className="bg-supernova-500 rounded-2xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Coins size={20} className="text-white" />
          </div>
          <div>
            <p className="text-xs text-white/70">QPUNTOS acumulados</p>
            <p className="text-2xl font-bold text-white leading-tight">
              {(user?.qpoints ?? 0).toLocaleString("es-CO")}
            </p>
          </div>
          <button
            onClick={() => setQrOpen(true)}
            className="ml-auto flex flex-col items-center gap-1 bg-white/20 hover:bg-white/30 active:scale-95 transition rounded-xl px-3 py-2 flex-shrink-0"
          >
            <QrCode size={20} className="text-white" />
            <span className="text-[10px] font-bold text-white/80 leading-none">Mi QR</span>
          </button>
        </div>

        {/* ══ ACCIONES PENDIENTES ══════════════════════════════ */}
        {pendingTasks.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-amber-100 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-b border-amber-100">
              <span className="text-amber-500">⚡</span>
              <p className="text-sm font-bold text-amber-800">
                {pendingTasks.length === 1 ? "1 acción pendiente" : `${pendingTasks.length} acciones pendientes`}
              </p>
            </div>
            <div className="divide-y divide-gray-50">
              {pendingTasks.map(t => {
                const colors = {
                  green:  { bg: "bg-green-100",  text: "text-green-600",  pill: "bg-green-100 text-green-700" },
                  blue:   { bg: "bg-blue-100",   text: "text-blue-600",   pill: "bg-blue-100 text-blue-700" },
                  purple: { bg: "bg-purple-100", text: "text-purple-600", pill: "bg-purple-100 text-purple-700" },
                }[t.color] || {};
                return (
                  <button key={t.id} onClick={t.action}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition text-left">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${colors.bg} ${colors.text}`}>
                      {t.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{t.label}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{t.hint}</p>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${colors.pill}`}>
                      {t.cta}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ INFORMACIÓN PERSONAL ════════════════════════════ */}
        <Section
          icon={<User size={17} className="text-supernova-500" />}
          title="Mi información"
          summary={form.name || "Nombre, teléfono y correo"}
          open={openSec === "profile"}
          onToggle={() => toggleSec("profile")}
        >
          <div className="space-y-4">
            {/* Nombre */}
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">Nombre completo</label>
              <input className={inp} value={form.name} placeholder="Tu nombre completo"
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>

            {/* Fecha de nacimiento */}
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">Fecha de nacimiento</label>
              <input
                type="date"
                className={inp}
                value={form.birthDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setForm(p => ({ ...p, birthDate: e.target.value }))}
              />
              {form.birthDate && (
                <p className="mt-1 text-xs text-gray-400">
                  {(() => {
                    const d = new Date(form.birthDate + "T00:00:00");
                    const age = Math.floor((Date.now() - d) / (365.25 * 24 * 3600 * 1000));
                    return `${d.toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" })} · ${age} años`;
                  })()}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">Correo electrónico</label>
              <div className="flex items-center gap-2 p-3 rounded-xl border border-gray-200 bg-gray-50">
                <span className="flex-1 text-sm text-gray-500 truncate">{user?.email}</span>
                {user?.emailVerified
                  ? <span className="flex items-center gap-1 text-xs font-semibold text-green-600 shrink-0"><CheckCircle2 size={12} /> Verificado</span>
                  : <span className="flex items-center gap-1 text-xs font-semibold text-amber-600 shrink-0"><Clock size={12} /> Sin verificar</span>
                }
              </div>
              {!user?.emailVerified && (
                <button onClick={resendEmail} disabled={resending}
                  className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-purple-50 border border-purple-200 text-purple-700 text-sm font-semibold hover:bg-purple-100 disabled:opacity-60 transition">
                  <Mail size={14} /> {resending ? "Enviando…" : "Reenviar correo de verificación"}
                </button>
              )}
            </div>

            {/* Teléfono / WhatsApp */}
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-2 mb-1.5">
                Teléfono / WhatsApp
                {user?.whatsappVerified && (
                  <span className="text-green-600 normal-case font-normal text-xs flex items-center gap-1">
                    <CheckCircle2 size={11} /> Verificado
                  </span>
                )}
              </label>
              <div className="flex gap-2">
                <select value={form.phoneCountryCode}
                  onChange={e => setForm(p => ({ ...p, phoneCountryCode: e.target.value }))}
                  className="p-3 rounded-xl border border-gray-200 focus:border-supernova-400 outline-none bg-white text-sm">
                  {COUNTRY_CODES.map(({ code, flag }) => (
                    <option key={code} value={code}>{flag} {code}</option>
                  ))}
                </select>
                <input className={`flex-1 ${inp}`} type="tel" placeholder="3001234567"
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              {!user?.whatsappVerified && form.phone && (
                <button onClick={sendOtp} disabled={otpLoading}
                  className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm font-semibold hover:bg-green-100 disabled:opacity-60 transition">
                  <MessageCircle size={14} /> {otpLoading ? "Enviando código…" : "Verificar por WhatsApp"}
                </button>
              )}
            </div>

            {/* Guardar */}
            <button onClick={savePersonal} disabled={saving}
              className="w-full py-3 rounded-xl bg-supernova-400 hover:bg-supernova-500 text-black font-bold transition disabled:opacity-60 flex items-center justify-center gap-2">
              {saving
                ? <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> Guardando…</>
                : "Guardar cambios"}
            </button>

            {/* OTP inline */}
            {otpOpen && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-green-800">
                    Código enviado a {form.phoneCountryCode} {form.phone}
                  </p>
                  <button onClick={() => setOtpOpen(false)} className="text-green-500 hover:text-green-700">
                    <X size={16} />
                  </button>
                </div>
                <input
                  className="w-full p-4 rounded-xl border-2 border-green-300 focus:border-green-500 outline-none text-center text-3xl tracking-[0.5em] font-bold transition bg-white"
                  maxLength={6} inputMode="numeric" placeholder="······"
                  value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, ""))} />
                <button onClick={verifyOtp} disabled={otpLoading || otpCode.length !== 6}
                  className="w-full py-3 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold disabled:opacity-50 transition">
                  {otpLoading ? "Verificando…" : "Confirmar código"}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  ¿No llegó?{" "}
                  <button onClick={sendOtp} className="text-supernova-600 font-semibold hover:underline">Reenviar</button>
                </p>
              </div>
            )}
          </div>
        </Section>

        {/* ══ DIRECCIONES ═════════════════════════════════════ */}
        <Section
          icon={<MapPin size={17} className="text-blue-500" />}
          title="Mis direcciones"
          summary={addresses.length === 0 ? "Sin direcciones — agrega una" : `${addresses.length} dirección${addresses.length !== 1 ? "es" : ""} guardada${addresses.length !== 1 ? "s" : ""}`}
          badge={addresses.length === 0 ? "Pendiente" : null}
          open={openSec === "addresses"}
          onToggle={() => toggleSec("addresses")}
        >
          {/* Botón agregar */}
          <div className="flex justify-end mb-3">
            <button onClick={() => setAddrOpen(o => o === "new" ? null : "new")}
              className="flex items-center gap-1.5 text-sm font-semibold text-supernova-600 bg-supernova-50 hover:bg-supernova-100 px-3 py-1.5 rounded-xl transition">
              <Plus size={14} /> Nueva dirección
            </button>
          </div>

          {addrOpen === "new" && (
            <div className="bg-gray-50 rounded-xl p-4 mb-3">
              <p className="text-sm font-semibold text-gray-700 mb-3">Nueva dirección</p>
              <AddressForm initial={{}} onSubmit={submitAddr} onCancel={() => setAddrOpen(null)} />
            </div>
          )}

          {addresses.length === 0 && addrOpen !== "new" && (
            <div className="py-8 flex flex-col items-center gap-2 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <MapPin size={22} className="text-gray-300" />
              </div>
              <p className="text-sm text-gray-400">No tienes direcciones guardadas</p>
            </div>
          )}

          <div className="space-y-2">
            {addresses.map(a => {
              const id = getId(a);
              return (
                <div key={id} className="bg-gray-50 rounded-xl p-3">
                  {delConfirm?.type === "address" && delConfirm?.id === id ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">¿Eliminar <strong>{a.label || a.address}</strong>?</p>
                      <div className="flex gap-2">
                        <button onClick={() => setDelConfirm(null)}
                          className="flex-1 py-2 rounded-xl border text-sm font-semibold text-gray-600 hover:bg-white transition">Cancelar</button>
                        <button onClick={doDelete}
                          className="flex-1 py-2 rounded-xl bg-red-500 text-white text-sm font-bold transition">Eliminar</button>
                      </div>
                    </div>
                  ) : addrOpen === id ? (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Editar dirección</p>
                      <AddressForm initial={a} onSubmit={submitAddr} onCancel={() => setAddrOpen(null)} />
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-800 text-sm">{a.label || "Dirección"}</p>
                          {a.isDefault && (
                            <span className="inline-flex items-center gap-1 text-[11px] bg-supernova-100 text-supernova-700 px-1.5 py-0.5 rounded-full font-semibold">
                              <Star size={8} /> Principal
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">{a.address}</p>
                        <p className="text-xs text-gray-400">{a.city}</p>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => setAddrOpen(id)}
                          className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:text-supernova-600 transition">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setDelConfirm({ type: "address", id })}
                          className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-400 hover:text-red-500 transition">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* ══ MÉTODOS DE PAGO ════════════════════════════════ */}
        <Section
          icon={<CreditCard size={17} className="text-green-500" />}
          title="Métodos de pago"
          summary={payments.length === 0 ? "Sin métodos — agrega uno" : `${payments.length} método${payments.length !== 1 ? "s" : ""} guardado${payments.length !== 1 ? "s" : ""}`}
          open={openSec === "payments"}
          onToggle={() => toggleSec("payments")}
        >
          <div className="flex justify-end mb-3">
            <button onClick={() => setPayOpen(o => o === "new" ? null : "new")}
              className="flex items-center gap-1.5 text-sm font-semibold text-supernova-600 bg-supernova-50 hover:bg-supernova-100 px-3 py-1.5 rounded-xl transition">
              <Plus size={14} /> Nuevo método
            </button>
          </div>

          {payOpen === "new" && (
            <div className="bg-gray-50 rounded-xl p-4 mb-3">
              <p className="text-sm font-semibold text-gray-700 mb-3">Nuevo método de pago</p>
              <PaymentForm initial={{}} onSubmit={submitPay} onCancel={() => setPayOpen(null)} />
            </div>
          )}

          {payments.length === 0 && payOpen !== "new" && (
            <div className="py-8 flex flex-col items-center gap-2 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <Wallet size={22} className="text-gray-300" />
              </div>
              <p className="text-sm text-gray-400">No tienes métodos de pago guardados</p>
            </div>
          )}

          <div className="space-y-2">
            {payments.map(p => {
              const id = getId(p);
              return (
                <div key={id} className="bg-gray-50 rounded-xl p-3">
                  {delConfirm?.type === "payment" && delConfirm?.id === id ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">¿Eliminar <strong>{PAYMENT_LABELS[p.type] || p.type}</strong>?</p>
                      <div className="flex gap-2">
                        <button onClick={() => setDelConfirm(null)}
                          className="flex-1 py-2 rounded-xl border text-sm font-semibold text-gray-600 hover:bg-white transition">Cancelar</button>
                        <button onClick={doDelete}
                          className="flex-1 py-2 rounded-xl bg-red-500 text-white text-sm font-bold transition">Eliminar</button>
                      </div>
                    </div>
                  ) : payOpen === id ? (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">Editar método</p>
                      <PaymentForm initial={p} onSubmit={submitPay} onCancel={() => setPayOpen(null)} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-lg flex-shrink-0">
                        {PAYMENT_ICONS[p.type] || "💳"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-800 text-sm">{PAYMENT_LABELS[p.type] || p.type}</p>
                          {p.isDefault && (
                            <span className="inline-flex items-center gap-1 text-[11px] bg-supernova-100 text-supernova-700 px-1.5 py-0.5 rounded-full font-semibold">
                              <Star size={8} /> Principal
                            </span>
                          )}
                        </div>
                        {p.type === "tarjeta"
                          ? <p className="text-xs text-gray-500">•••• {p.cardNumber?.slice(-4)} · {p.expires}</p>
                          : <p className="text-xs text-gray-500">{p.phone}</p>
                        }
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <button onClick={() => setPayOpen(id)}
                          className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:text-supernova-600 transition">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => setDelConfirm({ type: "payment", id })}
                          className="w-8 h-8 rounded-lg border border-gray-200 bg-white flex items-center justify-center text-gray-400 hover:text-red-500 transition">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Section>

        {/* ══ REFERIDOS ════════════════════════════════════════ */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 pt-4 pb-1">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Referidos · gana QPUNTOS</p>
          </div>
          <div className="px-4 pb-4 pt-2 space-y-3">
            {user?.referralCode ? (
              <div className="bg-supernova-50 border border-supernova-100 rounded-xl px-4 py-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] text-gray-500 mb-0.5">Tu código</p>
                    <p className="font-mono text-lg font-bold text-supernova-600 tracking-widest">{user.referralCode}</p>
                  </div>
                  <button onClick={copyCode}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-supernova-500 text-white text-xs font-semibold rounded-lg hover:bg-supernova-600 transition active:scale-95">
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? "Copiado" : "Copiar"}
                  </button>
                </div>
                <button onClick={shareReferral}
                  className="w-full flex items-center justify-center gap-2 py-2 border border-supernova-200 text-supernova-600 text-xs font-semibold rounded-lg hover:bg-supernova-100 transition active:scale-95">
                  <Share2 size={13} /> Compartir enlace de invitación
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-500">Genera tu código e invita amigos</p>
                <button onClick={generateCode}
                  className="flex-shrink-0 px-3 py-1.5 bg-supernova-500 text-white text-xs font-semibold rounded-lg hover:bg-supernova-600 transition">
                  Generar código
                </button>
              </div>
            )}
            {!user?.hasOrdered && !user?.referredBy && (
              <div>
                <p className="text-[11px] text-gray-400 mb-1.5">¿Te invitaron? Ingresa el código</p>
                <div className="flex gap-2">
                  <input type="text" placeholder="CÓDIGO" value={refCode}
                    onChange={e => setRefCode(e.target.value.toUpperCase())}
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-supernova-300" />
                  <button onClick={applyCode} disabled={!refCode}
                    className="px-3 py-2 rounded-xl text-xs font-semibold text-white bg-supernova-500 hover:bg-supernova-600 disabled:bg-gray-200 disabled:text-gray-400 transition">
                    Aplicar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ══ VERIFICACIÓN DE IDENTIDAD ═══════════════════════ */}
        <Section
          icon={<BadgeCheck size={17} className="text-purple-500" />}
          title="Verificación de identidad"
          summary={
            user?.verificationStatus === "aprobado" ? "✅ Identidad verificada" :
            user?.documentPhoto ? "⏳ Documento en revisión" : "Sube tu documento para el sello azul"
          }
          badge={user?.verificationStatus === "aprobado" ? "Verificado" : user?.documentPhoto ? "En revisión" : null}
          open={openSec === "identity"}
          onToggle={() => toggleSec("identity")}
        >
          <input ref={docInputRef} type="file" accept="image/*,.pdf" className="hidden"
            onChange={e => uploadDoc(e.target.files?.[0])} />

          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl mb-4">
            <BadgeCheck size={18} className="text-blue-500 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">Los usuarios verificados muestran el sello azul en su perfil.</p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">Tipo</label>
              <select value={form.documentType}
                onChange={e => setForm(p => ({ ...p, documentType: e.target.value }))}
                className={inp}>
                {["CC","CE","TI","PASAPORTE","V"].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">Número</label>
              <input className={inp} value={form.documentNumber} placeholder="123456789"
                onChange={e => setForm(p => ({ ...p, documentNumber: e.target.value }))} />
            </div>
          </div>

          {user?.documentPhoto ? (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                <img src={getImageUrl(user.documentPhoto)} alt="Documento"
                  className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 truncate">{user.documentPhoto.split("/").pop()}</p>
                {user.verificationStatus === "rechazado" && <p className="text-xs text-red-500 mt-0.5">❌ Rechazado. Sube uno nuevo.</p>}
                {user.verificationStatus === "pendiente" && user.documentPhoto && <p className="text-xs text-amber-600 mt-0.5">⏳ En revisión</p>}
                {user.verificationStatus === "aprobado" && <p className="text-xs text-blue-600 font-semibold mt-0.5">✅ Verificado</p>}
              </div>
              {user.verificationStatus !== "aprobado" && (
                <button onClick={() => docInputRef.current?.click()} disabled={docLoading}
                  className="text-xs text-supernova-600 font-semibold hover:underline whitespace-nowrap disabled:opacity-50">
                  {docLoading ? "Subiendo…" : "Cambiar"}
                </button>
              )}
            </div>
          ) : (
            <button onClick={() => docInputRef.current?.click()} disabled={docLoading}
              className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition disabled:opacity-50">
              <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Upload size={17} className="text-blue-500" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-700">{docLoading ? "Subiendo…" : "Subir foto del documento"}</p>
                <p className="text-xs text-gray-400">JPG, PNG o PDF · máx. 10 MB</p>
              </div>
            </button>
          )}

          <button onClick={savePersonal} disabled={saving}
            className="mt-4 w-full py-3 rounded-xl bg-supernova-400 hover:bg-supernova-500 text-black font-bold transition disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? <><span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> Guardando…</> : "Guardar"}
          </button>
        </Section>

        {/* ══ SEGURIDAD ════════════════════════════════════════ */}
        <Section
          icon={<Lock size={17} className="text-gray-500" />}
          title="Cambiar contraseña"
          summary="Seguridad de la cuenta"
          open={openSec === "security"}
          onToggle={() => toggleSec("security")}
        >
          <ChangePasswordForm onCancel={() => setOpenSec(null)} />
        </Section>

        {/* ══ MÁS OPCIONES ════════════════════════════════════ */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <MenuItem onClick={openSupport}
            icon={<MessageCircle size={17} className="text-green-600" />}
            iconBg="bg-green-50" label="Soporte por WhatsApp" desc="¿Necesitas ayuda? Escríbenos" />
          {user?.role === "user" && (
            <MenuItem to="/convert-to-ryder"
              icon={<Bike size={17} className="text-blue-600" />}
              iconBg="bg-blue-50" label="Ser domiciliario" desc="Gana dinero entregando pedidos" />
          )}
          {user?.role === "user" && (
            <MenuItem to="/register-business"
              icon={<Store size={17} className="text-violet-600" />}
              iconBg="bg-violet-50" label="Registrar comercio" desc="Vende en Qkiere.com" />
          )}
        </div>

        {/* ══ LOGOUT ══════════════════════════════════════════ */}
        <button onClick={logout}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-gray-200 text-sm font-semibold text-gray-400 hover:text-red-500 hover:border-red-200 transition">
          <LogOut size={15} /> Cerrar sesión
        </button>

        <p className="text-center text-[11px] text-gray-300 pb-2">© {new Date().getFullYear()} Qkiere.com</p>

      </div>
      <MobileBottomBar />

      {/* ══ MODAL: QR de usuario para canje de QPUNTOS ══ */}
      {qrOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setQrOpen(false)}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-xs overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <QrCode size={16} className="text-supernova-500" />
                <p className="font-bold text-gray-900 text-sm">Mi QR de QPUNTOS</p>
              </div>
              <button
                onClick={() => setQrOpen(false)}
                className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition"
              >
                <X size={15} />
              </button>
            </div>

            {/* QR */}
            <div className="flex flex-col items-center gap-4 px-6 py-7">
              <div className="bg-white p-4 rounded-2xl border-2 border-gray-100 shadow-inner">
                <QRCode
                  value={user?.id ?? ""}
                  size={180}
                  fgColor="#0d0f14"
                  bgColor="#ffffff"
                  level="M"
                />
              </div>

              <div className="text-center">
                <p className="font-bold text-gray-900 text-base leading-tight">{user?.name}</p>
                <p className="text-xs text-gray-400 mt-0.5 font-mono">{user?.id}</p>
              </div>

              <div className="w-full bg-supernova-50 border border-supernova-100 rounded-xl px-4 py-3 flex items-center gap-2">
                <Coins size={15} className="text-supernova-500 shrink-0" />
                <div>
                  <p className="text-xs font-bold text-supernova-700">
                    {(user?.qpoints ?? 0).toLocaleString("es-CO")} QPUNTOS disponibles
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    Muestra este código al cajero para canjear
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MODAL: agregar dirección (aparece automáticamente si no tiene ninguna) ══ */}
      {addrModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4 pb-[72px] sm:pb-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl flex flex-col max-h-[80vh] sm:max-h-[90vh]">
            {/* Header fijo */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
              <div>
                <p className="font-bold text-gray-900 text-sm">Agrega tu dirección</p>
                <p className="text-xs text-gray-400 mt-0.5">Necesaria para recibir tus pedidos</p>
              </div>
              <button onClick={() => setAddrModalOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 transition">
                <X size={16} />
              </button>
            </div>
            {/* Contenido scrollable */}
            <div className="px-5 py-4 overflow-y-auto">
              <AddressForm
                initial={{}}
                onSubmit={async (data) => {
                  try {
                    await API.post("/users/me/address", data);
                    toast.success("Dirección guardada");
                    setAddrModalOpen(false);
                    await fetchProfile();
                  } catch { toast.error("Error guardando dirección"); }
                }}
                onCancel={() => setAddrModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
